<template>
  <div :class="['loan-config-id', variant, className]">
    <span v-if="showLabel" class="loan-config-id__label">{{ label }}:</span>
    <span class="loan-config-id__value">{{ id }}</span>
    <button
      @click="handleCopy"
      class="loan-config-id__copy-btn"
      :title="copied ? 'Copied!' : 'Copy to clipboard'"
      :aria-label="copied ? 'Copied to clipboard' : 'Copy configuration ID'"
    >
      <transition name="icon-fade" mode="out-in">
        <img
          v-if="copied"
          key="success"
          src="@/assets/img/success-circle.svg"
          alt="Copied"
          class="loan-config-id__icon loan-config-id__icon--success"
        />
        <img
          v-else
          key="copy"
          src="@/assets/img/copy.svg"
          alt="Copy"
          class="loan-config-id__icon"
        />
      </transition>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "LoanConfigIdCopy",
  props: {
    id: {
      type: [String, Number],
      required: true,
      validator: (value) => value !== null && value !== undefined && value !== '',
    },
    label: {
      type: String,
      default: "Configuration ID",
    },
    className: {
      type: String,
      default: "",
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: String,
      default: "default",
      validator: (value) => ["default", "minimal", "card"].includes(value),
    },
    copyDuration: {
      type: Number,
      default: 2000,
      validator: (value) => value > 0 && value <= 10000,
    },
  },
  data() {
    return {
      copied: false,
      copyTimeout: null,
    };
  },
  computed: {
    ...mapGetters("organisationModule", ["loanConfiguration"]),
  },
  beforeDestroy() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  },
  methods: {
    async handleCopy() {
      if (this.copied) return;

      try {
        await navigator.clipboard.writeText(this.id.toString());
        this.setCopiedState();
      } catch (err) {
        console.warn("Clipboard API failed, using fallback:", err);
        this.fallbackCopy();
      }
    },
    
    fallbackCopy() {
      const textArea = document.createElement("textarea");
      textArea.value = this.id.toString();
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand("copy");
        if (successful) {
          this.setCopiedState();
        } else {
          throw new Error("Copy command failed");
        }
      } catch (err) {
        console.error("Fallback copy failed:", err);
        this.$emit('copy-error', err);
      } finally {
        document.body.removeChild(textArea);
      }
    },
    
    setCopiedState() {
      this.copied = true;
      this.$emit('copied', this.id);
      
      if (this.copyTimeout) {
        clearTimeout(this.copyTimeout);
      }
      
      this.copyTimeout = setTimeout(() => {
        this.copied = false;
        this.copyTimeout = null;
      }, this.copyDuration);
    },
  },
};
</script>

<style scoped>
.loan-config-id {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid;
  transition: all 0.2s ease-in-out;
  font-family: inherit;
}

.loan-config-id.default {
  background-color: #f9fafb;
  border-color: #e5e7eb;
  color: #374151;
}

.loan-config-id.minimal {
  background-color: transparent;
  border-color: transparent;
  color: #4b5563;
}

.loan-config-id.card {
  background-color: #ffffff;
  border-color: #e5e7eb;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.loan-config-id.card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.loan-config-id__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}

.loan-config-id__value {
  font-family: "Courier New", "Monaco", "Menlo", "Consolas", monospace;
  font-size: 0.875rem;
  user-select: all;
  -webkit-user-select: all;
  -moz-user-select: all;
  -ms-user-select: all;
}

.loan-config-id__copy-btn {
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-width: 2rem;
  min-height: 2rem;
}

.loan-config-id__copy-btn:hover {
  background-color: #f3f4f6;
}

.loan-config-id__copy-btn:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.loan-config-id__copy-btn:active {
  transform: scale(0.95);
  transition: transform 0.1s ease-in-out;
}

.loan-config-id__icon {
  width: 1rem;
  height: 1rem;
  transition: all 0.15s ease-in-out;
  filter: opacity(0.6);
}

.loan-config-id__copy-btn:hover .loan-config-id__icon {
  filter: opacity(1);
}

.loan-config-id__icon--success {
  filter: opacity(1);
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: all 0.2s ease-in-out;
}

.icon-fade-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.icon-fade-leave-to {
  opacity: 0;
  transform: scale(1.2);
}

.icon-fade-enter-to,
.icon-fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

@media (max-width: 640px) {
  .loan-config-id {
    padding: 0.375rem 0.75rem;
    gap: 0.5rem;
  }

  .loan-config-id__label,
  .loan-config-id__value {
    font-size: 0.8125rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .loan-config-id,
  .loan-config-id__copy-btn,
  .loan-config-id__icon,
  .icon-fade-enter-active,
  .icon-fade-leave-active {
    transition: none;
  }
}

.loan-config-id__copy-btn:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
</style>